import React, { useMemo } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments } from '@modernloop/shared/components';
import { useFlag } from '@modernloop/shared/feature-flag';
import { Avatar, Chip, Stack, Typography } from '@mui/material';

import {
  DynamicInterviewMeetingHost,
  InterviewMeetingLocationInfo_InterviewMeetingLocationSettingPrefFragment,
  InterviewMeetingLocationType,
  SettingLevel,
  VideoMeetingLinkScope,
} from 'src/generated/mloop-graphql';

import { OrgSettingsSource } from 'src/constants/routes';

import { SyncedFromOrgSettingsWrapper } from '../common/SyncedFromOrgSettingsWrapper';

import { InterviewMeetingLocationVariant } from './types';
import {
  getInterviewMeetingLocationIcon,
  getInterviewMeetingLocationLabelKey,
  isLinkBearingMeetingLocationType,
} from './utils';

type Fragments = {
  interviewMeetingLocationSettingPref:
    | InterviewMeetingLocationInfo_InterviewMeetingLocationSettingPrefFragment
    | undefined;
};

type Props = {
  level?: SettingLevel;
  variant?: InterviewMeetingLocationVariant;
};

/**
 * Read-only summary of an interview meeting-location setting (location, host, and — when the
 * `org_video_meeting_link_scope` flag is on and the location is link-bearing — the video meeting
 * link scope).
 *
 * Pass `variant: DEBRIEF` for a debrief location setting: the link scope is never summarized (a
 * debrief is one meeting) and the "synced from org settings" link points at the Debrief Location page.
 */
const InterviewMeetingLocationInfo: FCWithFragments<Fragments, Props> = ({
  interviewMeetingLocationSettingPref,
  level,
  variant = InterviewMeetingLocationVariant.INTERVIEW,
}) => {
  const { t } = useTranslation();
  const orgVideoMeetingLinkScopeEnabled = useFlag('org_video_meeting_link_scope');
  const isDebrief = variant === InterviewMeetingLocationVariant.DEBRIEF;
  const label = useMemo(() => {
    const labelKey = getInterviewMeetingLocationLabelKey(
      interviewMeetingLocationSettingPref?.interviewMeetingLocationType
    );
    return labelKey ? t(labelKey) : '';
  }, [interviewMeetingLocationSettingPref?.interviewMeetingLocationType, t]);

  const icon = useMemo(
    () => getInterviewMeetingLocationIcon(interviewMeetingLocationSettingPref?.interviewMeetingLocationType),
    [interviewMeetingLocationSettingPref?.interviewMeetingLocationType]
  );

  const displayInterviewMeetingLocationLabel = useMemo(() => {
    if (!interviewMeetingLocationSettingPref) {
      return true;
    }

    return (
      ![
        InterviewMeetingLocationType.CustomLink,
        InterviewMeetingLocationType.CustomPhone,
        InterviewMeetingLocationType.CustomText,
      ].includes(interviewMeetingLocationSettingPref?.interviewMeetingLocationType) ||
      !interviewMeetingLocationSettingPref.customLocation
    );
  }, [interviewMeetingLocationSettingPref]);

  const host = useMemo(() => {
    if (!interviewMeetingLocationSettingPref) {
      return null;
    }
    if (interviewMeetingLocationSettingPref.dynamicHost === DynamicInterviewMeetingHost.Interviewers) {
      return <Typography>{t('hiring-dashboard-common:common.location.interviewers')}</Typography>;
    }
    if (interviewMeetingLocationSettingPref.dynamicHost === DynamicInterviewMeetingHost.Scheduler) {
      return <Typography>{t('hiring-dashboard-common:common.location.scheduler')}</Typography>;
    }
    if (interviewMeetingLocationSettingPref.hostEmployee) {
      return (
        <Chip
          avatar={<Avatar src={interviewMeetingLocationSettingPref.hostEmployee.internalProfilePictureUrl || ''} />}
          label={
            interviewMeetingLocationSettingPref.hostEmployee.fullName ||
            interviewMeetingLocationSettingPref.hostEmployee.email
          }
        />
      );
    }
    if (interviewMeetingLocationSettingPref.remoteVideoMeetingHostUser) {
      return (
        <Typography>
          {interviewMeetingLocationSettingPref.remoteVideoMeetingHostUser?.firstName &&
          interviewMeetingLocationSettingPref.remoteVideoMeetingHostUser?.lastName
            ? `${interviewMeetingLocationSettingPref.remoteVideoMeetingHostUser.firstName} ${interviewMeetingLocationSettingPref.remoteVideoMeetingHostUser.lastName}`
            : interviewMeetingLocationSettingPref.remoteVideoMeetingHostUser?.email}
        </Typography>
      );
    }
    return null;
  }, [interviewMeetingLocationSettingPref, t]);

  // Summarize the video-meeting-link scope (flag-gated) only for link-bearing locations where it is
  // meaningful and explicitly resolved. Interviewer-hosted locations are structurally unique.
  const videoMeetingLinkScopeLabel = useMemo(() => {
    // A debrief is a single meeting, so the one-link vs. unique-link choice never applies to it.
    if (isDebrief || !orgVideoMeetingLinkScopeEnabled || !interviewMeetingLocationSettingPref) {
      return null;
    }
    if (!isLinkBearingMeetingLocationType(interviewMeetingLocationSettingPref.interviewMeetingLocationType)) {
      return null;
    }

    const isInterviewersHost =
      interviewMeetingLocationSettingPref.dynamicHost === DynamicInterviewMeetingHost.Interviewers;
    if (
      isInterviewersHost ||
      interviewMeetingLocationSettingPref.videoMeetingLinkScope === VideoMeetingLinkScope.UniqueLinkPerInterview
    ) {
      return t('hiring-dashboard-schedule:schedule.communications.uniqueForEachInterview');
    }
    if (interviewMeetingLocationSettingPref.videoMeetingLinkScope === VideoMeetingLinkScope.OneLink) {
      return t('hiring-dashboard-schedule:schedule.communications.sameForAllInterviews');
    }
    return null;
  }, [isDebrief, orgVideoMeetingLinkScopeEnabled, interviewMeetingLocationSettingPref, t]);

  return (
    <Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        {icon}
        {displayInterviewMeetingLocationLabel && <Typography color="text.secondary">{label}</Typography>}
        {!displayInterviewMeetingLocationLabel && (
          <Typography color="text.secondary">{interviewMeetingLocationSettingPref?.customLocation}</Typography>
        )}
      </Stack>
      {host && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography>{t('hiring-dashboard-common:common.host')}</Typography>
          {host}
        </Stack>
      )}
      {videoMeetingLinkScopeLabel && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography>{t('hiring-dashboard-common:common.location.videoMeetingLinks')}</Typography>
          <Typography color="text.secondary">{videoMeetingLinkScopeLabel}</Typography>
        </Stack>
      )}
      {level && (
        <SyncedFromOrgSettingsWrapper
          source={isDebrief ? OrgSettingsSource.DEBRIEF_LOCATION : OrgSettingsSource.SCHEDULE_LOCATION}
          level={level}
        />
      )}
    </Stack>
  );
};

InterviewMeetingLocationInfo.fragments = {
  interviewMeetingLocationSettingPref: gql`
    fragment InterviewMeetingLocationInfo_interviewMeetingLocationSettingPref on InterviewMeetingLocationSettingPref {
      interviewMeetingLocationType
      videoMeetingLinkScope
      dynamicHost
      hostEmployee {
        id
        fullName
        email
        internalProfilePictureUrl
      }
      remoteVideoMeetingHostUser {
        ... on ZoomUser {
          zoomUserId
          firstName
          lastName
          email
        }
      }
      customLocation
    }
  `,
};

export default InterviewMeetingLocationInfo;
